import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useId, useState, } from "react";
import { CheckIcon, EyeIcon, EyeOffIcon, LockIcon } from "./Icons.js";
import { scorePassword, strengthTone } from "../tokens.js";
export function TextField({ eyebrow, helper, invalid = false, adornment, eyebrowAction, id, className, ...rest }) {
    const generated = useId();
    const inputId = id ?? generated;
    const helperId = `${inputId}-helper`;
    return (_jsxs("div", { className: ["wr-field", className ?? ""].filter(Boolean).join(" "), children: [_jsxs("div", { className: "wr-row", style: { justifyContent: "space-between", minHeight: 24 }, children: [_jsx("label", { className: "wr-mono-xs wr-field__eyebrow", htmlFor: inputId, children: eyebrow }), eyebrowAction] }), _jsxs("div", { className: "wr-field__row", children: [_jsx("input", { id: inputId, className: "wr-field__input", "aria-invalid": invalid || undefined, "aria-describedby": helper ? helperId : undefined, ...rest }), adornment ? _jsx("span", { className: "wr-field__adornment", children: adornment }) : null] }), helper ? (_jsx("p", { id: helperId, className: [
                    "wr-mono-sm",
                    "wr-field__helper",
                    invalid ? "wr-field__helper--error" : "",
                ].join(" "), "aria-live": "polite", children: helper })) : null] }));
}
/**
 * `TextField` plus a reveal toggle in the adornment slot (§6).
 *
 * The toggle is a real button with a real label; it is not a tab-stop-free
 * icon, because "did I type my password correctly" is exactly the question a
 * keyboard user has too.
 */
export function PasswordField({ revealLabel, hideLabel, adornment, ...rest }) {
    const [revealed, setRevealed] = useState(false);
    return (_jsx(TextField, { ...rest, type: revealed ? "text" : "password", adornment: _jsxs(_Fragment, { children: [adornment, _jsx("button", { type: "button", className: "wr-icon-btn", style: { width: 34, height: 34 }, "aria-pressed": revealed, "aria-label": revealed ? hideLabel : revealLabel, title: revealed ? hideLabel : revealLabel, onClick: () => setRevealed((value) => !value), children: revealed ? _jsx(EyeOffIcon, {}) : _jsx(EyeIcon, {}) })] }) }));
}
/**
 * Four cells and a requirement list (§6).
 *
 * The list is the point: a bar that goes from coral to gold with no explanation
 * tells a 15-year-old they failed at something without saying what.
 */
export function StrengthMeter({ password, minLength, ruleLabels, reason }) {
    const strength = scorePassword(password, minLength);
    const tone = strengthTone(strength.score);
    return (_jsxs("div", { className: "wr-strength", children: [_jsx("div", { className: "wr-strength__bar", "aria-hidden": "true", children: [0, 1, 2, 3].map((index) => (_jsx("span", { className: [
                        "wr-strength__cell",
                        index < strength.score ? `wr-strength__cell--${tone}` : "",
                    ]
                        .filter(Boolean)
                        .join(" ") }, index))) }), _jsx("ul", { className: "wr-strength__rules", children: Object.keys(ruleLabels).map((rule) => (_jsxs("li", { className: [
                        "wr-mono-sm",
                        "wr-strength__rule",
                        strength.met[rule] ? "wr-strength__rule--met" : "",
                    ]
                        .filter(Boolean)
                        .join(" "), children: [_jsx("span", { className: "wr-strength__check", children: _jsx(CheckIcon, { size: 14 }) }), ruleLabels[rule]] }, rule))) }), reason ? _jsx("p", { className: "wr-mono-sm wr-ember", children: reason }) : null] }));
}
export function Checkbox({ label, id, className, ...rest }) {
    const generated = useId();
    const inputId = id ?? generated;
    return (_jsxs("label", { className: ["wr-checkbox", className ?? ""].filter(Boolean).join(" "), htmlFor: inputId, children: [_jsxs("span", { style: { position: "relative", display: "inline-flex" }, children: [_jsx("input", { id: inputId, type: "checkbox", className: "wr-checkbox__input", ...rest }), _jsx("span", { className: "wr-checkbox__box", "aria-hidden": "true", children: _jsx(CheckIcon, { size: 16 }) })] }), _jsx("span", { className: "wr-body-sm wr-checkbox__label", children: label })] }));
}
/**
 * **LOCKED (§9 guest variant): a locked value is NOT a disabled input.**
 *
 * A greyed-out text box invites a tap that does nothing and teaches a guest
 * that the app is broken for them. This is a plate with a padlock: it reads as
 * "this is fixed", not as "this is failing".
 */
export function LockedValue({ eyebrow, value, lockLabel, helper }) {
    return (_jsxs("div", { className: "wr-field", children: [_jsx("span", { className: "wr-mono-xs wr-field__eyebrow", children: eyebrow }), _jsxs("div", { className: "wr-locked-value", children: [_jsx("span", { className: "wr-body-lg", children: value }), _jsx(LockChip, { label: lockLabel, className: "wr-locked-value__chip" })] }), helper ? _jsx("p", { className: "wr-mono-sm wr-field__helper", children: helper }) : null] }));
}
/**
 * **The lock chip — S2 §22.11, promoted out of `LockedValue` so the two can
 * never drift.**
 *
 * > *"Never ship an accessible name without the object it names."* As built, a
 * > screen-reader guest was told "Guest nickname, can't be changed" and a
 * > sighted guest was told nothing — they had to infer the rule from the
 * > **absence** of a control.
 *
 * ── The rules that are binding on every use (§22.11.1) ──────────────────────
 *
 *  - **20×20, the existing padlock, `ink-70`, `flex: none`, no bed, border or
 *    radius.** One rule set, `.wr-lock-chip`, which `.wr-locked-value__chip`
 *    composes. No new art (§22.4's habit).
 *  - **It is not a control.** No `tabindex`, not a `<button>`, and **no
 *    `title`** — a title tooltip is unreachable on touch and is exactly N2's
 *    failure mode. The glyph is `aria-hidden`; the name is the visually-hidden
 *    span.
 *  - **Where it renders is the caller's rule, not this component's**
 *    (§22.11.2: exactly when the card offers no way to edit the value at all).
 */
export function LockChip({ label, className, testId }) {
    return (_jsxs("span", { className: ["wr-lock-chip", className ?? ""].filter(Boolean).join(" "), "data-testid": testId, children: [_jsx(LockIcon, { size: 20 }), _jsx("span", { className: "wr-visually-hidden", children: label })] }));
}
/** Max 3 chips (§9). Tapping one fills the field and re-runs the check. */
export function SuggestionChips({ suggestions, onPick, label }) {
    if (suggestions.length === 0)
        return null;
    return (_jsx("div", { className: "wr-chips", role: "group", "aria-label": label, children: suggestions.slice(0, 3).map((suggestion) => (_jsx("button", { type: "button", className: "wr-chip wr-mono-sm", onClick: (() => onPick(suggestion)), children: suggestion }, suggestion))) }));
}
export function SegmentedToggle({ options, value, onChange, label, disabled = false, }) {
    return (_jsx("div", { className: "wr-segmented", role: "tablist", "aria-label": label, children: options.map((option) => (_jsx("button", { type: "button", role: "tab", "aria-selected": option.value === value, className: "wr-segmented__option", disabled: disabled, onClick: () => onChange(option.value), children: option.label }, option.value))) }));
}
//# sourceMappingURL=Fields.js.map